// Formularzustand und Feldnamen (DE/EN) der oeffentlichen Self-Service-Flaeche.
import type { Lang } from './i18n/lang';
//
// Der Typ liegt hier und nicht in `SelfEditPage.tsx`, damit Formular und Labels
// dieselbe Quelle haben: `Record<keyof FormState, string>` macht ein neues
// Formularfeld ohne Label zum Compilerfehler. (Andersherum — Labels im Page-
// Modul — zoege der Labels-Test die ganze Seite samt jsdom mit hinein.)
//
// Gebraucht werden die Namen dort, wo ein Feld OHNE sein Eingabefeld benannt
// wird: Konfliktdialog („Diese Felder wurden von beiden Seiten unterschiedlich
// geaendert: …") und Merge-Toast („Zusammengeführt: Mobil von TRO uebernommen.").
//
// Bewusst eigene Liste statt `voiceFieldLabels.ts`: die Self-Service-Flaeche
// liegt ausserhalb von Auth, hat ihr eigenes Bundle und darf kein Admin-Modul
// importieren — und sie zeigt andere Beschriftungen (keine `(intern)`-Marker,
// „Sprachen" statt „Sprachen der Sprecher:in").

/** Die Felder, die eine Sprecher:in selbst pflegen darf (Allowlist, Etappe 7). */
export interface FormState {
  last_name: string;
  first_name: string;
  phone: string;
  phone_landline: string;
  email: string;
  web: string;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  eigenes_studio: boolean;
  languages: string[];
  dialects: string[];
  accents: string[];
}

// Verbindlich ist die SICHTBARE Beschriftung aus `SelfEditPage.tsx`
// (Entscheidung 2026-09-15, wie bei den Admin-Labels): wer im Dialog einen
// Feldnamen liest, muss das Feld auf der Seite wiederfinden — deshalb
// „Mögliche Dialekte" statt der Kurzform „Dialekte" aus der Spec-Aufzaehlung.
// `eigenes_studio` traegt den Gegenstand ohne das Satzende der Checkbox
// („Eigenes Studio vorhanden"), damit es sich in einer Aufzaehlung liest.
export const SELF_FIELD_LABELS: Record<keyof FormState, string> = {
  last_name: 'Nachname',
  first_name: 'Vorname',
  phone: 'Mobil',
  phone_landline: 'Telefon',
  email: 'E-Mail',
  web: 'Web',
  strasse: 'Straße',
  plz: 'PLZ',
  ort: 'Ort',
  land: 'Land',
  eigenes_studio: 'Eigenes Studio',
  languages: 'Sprachen',
  dialects: 'Mögliche Dialekte',
  accents: 'Mögliche Akzente',
};

// Englische Tabelle (Spec 2026-09-15 Sprachumschalter): dieselbe Regel wie oben —
// wortgleich mit der sichtbaren englischen Beschriftung der Seite.
export const SELF_FIELD_LABELS_EN: Record<keyof FormState, string> = {
  last_name: 'Last name',
  first_name: 'First name',
  phone: 'Mobile',
  phone_landline: 'Phone',
  email: 'Email',
  web: 'Website',
  strasse: 'Street',
  plz: 'Postcode',
  ort: 'City',
  land: 'Country',
  eigenes_studio: 'Own studio',
  languages: 'Languages',
  dialects: 'Possible dialects',
  accents: 'Possible accents',
};

/** Feldnamen-Tabelle der gewaehlten Sprache. */
export function selfFieldLabels(lang: Lang = 'de'): Record<keyof FormState, string> {
  return lang === 'en' ? SELF_FIELD_LABELS_EN : SELF_FIELD_LABELS;
}

/**
 * Feldschluessel zu einer lesbaren Aufzaehlung: `['phone','email']` →
 * „Mobil, E-Mail". Die uebergebene Reihenfolge bleibt erhalten; ein unbekannter
 * Schluessel faellt auf seinen technischen Namen zurueck, statt eine Luecke im
 * Satz zu hinterlassen.
 */
export function selfLabelsFor(keys: (keyof FormState)[], lang: Lang = 'de'): string {
  const labels = selfFieldLabels(lang);
  return keys.map((k) => labels[k] ?? String(k)).join(', ');
}
